import "./QuestionBoxVideoMention.css";
import {
	ATOM_GLYPH_CLASS,
	createPendingAtomLeadingVisual,
	createQuestionBoxAtom,
} from "./QuestionBoxAtom";

/**
 * Data needed to render and serialise a video mention inside the QuestionBox
 * input. Kept deliberately independent of the chat layer's richer
 * `MentionedVideoItem` so the design-system component stays decoupled — callers
 * map their own shape onto this.
 */
export type QuestionBoxVideoMentionData = {
	id: string;
	/** Text shown in the pill (already resolved, e.g. title or "Video 3"). */
	label: string;
	thumbnailUrl?: string | null;
	startTimeSec?: number | null;
	endTimeSec?: number | null;
};

/** Identity of a clip in the QuestionBox: source video plus time range. */
export function questionBoxVideoMentionKey(
	mention: Pick<QuestionBoxVideoMentionData, "id" | "startTimeSec" | "endTimeSec">,
): string {
	return `${mention.id}:${mention.startTimeSec ?? ""}:${mention.endTimeSec ?? ""}`;
}

/**
 * Whether the video stands for a range within itself rather than for the whole
 * of it: a whole-video mention carries no bounds at all, which is what separates
 * it from a clip whose range happens to start at the top of the video (the same
 * test as the chat layer's `isMentionClip`).
 */
export function isQuestionBoxVideoClip(
	video: Pick<QuestionBoxVideoMentionData, "startTimeSec" | "endTimeSec">,
): boolean {
	return video.startTimeSec != null && video.endTimeSec != null;
}

/** The scissors a clip wears, the counterpart of the VideoMentionMarker's. */
function clipMarker(): HTMLElement {
	const marker = document.createElement("span");
	marker.className = `question-box-atom__clip-symbol ${ATOM_GLYPH_CLASS}`;
	marker.setAttribute("aria-hidden", "true");
	return marker;
}

/**
 * Builds a video-mention pill for insertion into the QuestionBox contenteditable:
 * the purple variant of the shared atom (see QuestionBoxAtom), led by the video's
 * thumbnail.
 */
export function createQuestionBoxVideoMention(
	data: QuestionBoxVideoMentionData,
	options: { onRemove: (element: HTMLElement) => void },
): HTMLElement {
	return createQuestionBoxAtom({
		variant: "video",
		label: data.label,
		leading: createPendingAtomLeadingVisual(data.thumbnailUrl),
		marker: isQuestionBoxVideoClip(data) ? clipMarker() : undefined,
		data: {
			videoMentionId: data.id,
			videoMentionLabel: data.label,
			videoMentionThumbnail: data.thumbnailUrl || undefined,
			videoMentionStart:
				data.startTimeSec != null ? String(data.startTimeSec) : undefined,
			videoMentionEnd:
				data.endTimeSec != null ? String(data.endTimeSec) : undefined,
		},
		onRemove: options.onRemove,
	});
}

/** True when `node` is a video-mention pill produced by this module. */
export function isVideoMentionElement(node: Node): node is HTMLElement {
	return (
		node instanceof HTMLElement && node.dataset.videoMentionId !== undefined
	);
}
